import { EnumUtility } from '@codequality/vue-cli-base';

import { E0円非表示フラグ, Eモニタリング業績予測基準値, E計画表示モード } from '@/openapi/enum';
import { データListItemResponse } from '@/openapi/user';

import 決算期Utility, { 決算期TableHeader } from '@/classes/common/決算期Utility';
import AbstractPlbscfService, {
  PlbscfListItem,
  PlbscfTypeList,
  PlbscfYearItem,
  YearItemType,
} from '@/classes/service/AbstractPlbscfService';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import {
  Bs,
  Pl,
  Plbscf業績予測ListItem,
  帳票Type,
  業績予測,
  科目別計画ListItem,
  計画Plbscf,
} from '@/classes/service/Abstract計画PlbscfService';
import AbstractMonitoringService from '@/classes/service/モニタリング/AbstractMonitoringService';
import { PlbscfType, ValueType } from '@/master/Plbscf';

// ------------------------------------------------------------------
// 業績予測Service
// ------------------------------------------------------------------
export default class 業績予測Service extends AbstractMonitoringService<計画Plbscf> {
  // ------------------------------------------------------------------
  // fields
  // ------------------------------------------------------------------
  protected serviceType = ServiceType.業績予測;

  /** ヘッダー */
  private yearlyHeader: 決算期TableHeader = {
    label: '',
    year: 0,
    決算年: 0,
  };
  private monthlyHeaders: 決算期TableHeader[] = [];
  private 業績予測Headers: Record<keyof typeof Eモニタリング業績予測基準値, 決算期TableHeader[]> = {
    前期: [],
    計画: [],
  };

  // ------------------------------------------------------------------
  // public static methods
  // ------------------------------------------------------------------

  /**
   * 業績予測基準値に応じて、listを加工して返却
   *
   * @param list
   * @param e業績予測基準値
   * @returns
   */
  public static getVisibleListBy基準値(
    list: Plbscf業績予測ListItem[],
    e業績予測基準値: Eモニタリング業績予測基準値
  ): Plbscf業績予測ListItem[] {
    return list.map((listItem) => {
      const config = listItem.config;
      const yearValues = this.createYearItems(listItem.yearValues, listItem.monitoring!.yearValue, e業績予測基準値);
      return {
        ...listItem,
        yearValues,
        業績予測:
          config.valueType !== ValueType.noValue && config.unitNotYen === undefined
            ? this.calculate業績予測ByYearItems(yearValues)
            : {},
      };
    });
  }

  /**
   * 業績予測基準値に応じて、listを加工して返却
   *
   * @param list
   * @param e業績予測基準値
   * @returns
   */
  public static getVisibleCfListBy基準値(
    list: PlbscfListItem[],
    e業績予測基準値: Eモニタリング業績予測基準値
  ): PlbscfListItem[] {
    return list.map((listItem) => ({
      ...listItem,
      yearValues: this.createYearItems(listItem.yearValues, listItem.monitoring!.yearValue, e業績予測基準値),
    }));
  }

  /**
   * 業績予測基準値に応じて、listを加工して返却
   *
   * @param list
   * @param e業績予測基準値
   * @returns
   */
  public static getVisible科目別ListBy基準値(
    list: 科目別計画ListItem[],
    e業績予測基準値: Eモニタリング業績予測基準値
  ): 科目別計画ListItem[] {
    return list.map((listItem) => {
      const yearItems = this.createYearItems(listItem.yearItems, listItem.monitoring!.yearItem, e業績予測基準値);
      return {
        ...listItem,
        yearItems,
        monitoring: {
          ...listItem.monitoring!,
          業績予測: this.calculate業績予測ByYearItems(yearItems),
        },
      };
    });
  }

  /**
   * 業績予測基準値に応じて、yearItemsを加工して返却
   *
   * @param yearItems
   * @param monitoringYearItem
   * @param e業績予測基準値
   * @returns
   */
  private static createYearItems(
    yearItems: PlbscfYearItem[],
    monitoringYearItem: PlbscfYearItem,
    e業績予測基準値: Eモニタリング業績予測基準値
  ): PlbscfYearItem[] {
    return e業績予測基準値 === Eモニタリング業績予測基準値.前期
      ? yearItems
      : [monitoringYearItem, ...yearItems.slice(1)];
  }

  /**
   * yearItemsから業績予測を計算
   *
   * @param yearItems
   * @returns
   */
  private static calculate業績予測ByYearItems(yearItems: PlbscfYearItem[]): 業績予測 {
    return this.calculate業績予測(
      AbstractPlbscfService.getYearValue(yearItems[1]),
      AbstractPlbscfService.getYearValue(yearItems[0])
    );
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): 計画Plbscf {
    // 帳票かつ既に計算済の場合は再計算しない
    if (this.is帳票 && 業績予測Service.計画Plbscf !== null) return 業績予測Service.計画Plbscf;

    // 初期化
    this.initializeService(データs, option);

    // 画面表示用データを生成
    const v計画Plbscf = this.calcAndCreate計画Plbscf();

    // 帳票で再計算させないために詰めておく
    業績予測Service.計画Plbscf = v計画Plbscf;
    return v計画Plbscf;
  }

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): 帳票データ[] {
    const v計画Plbscf = this.parse(データs, option);

    if (option === undefined) throw new Error('no option');
    const plbscfType = option.plbscfType as PlbscfType;
    const v帳票Type = option.帳票Type as 帳票Type;

    // Eモニタリング業績予測基準値 に応じて出力する値を決める
    const e業績予測基準値 = option.referenceValue as Eモニタリング業績予測基準値;
    const 基準値Header: 決算期TableHeader = {
      label: EnumUtility.valueToKey(Eモニタリング業績予測基準値, e業績予測基準値),
      isSummary: true,
      year: 0,
      決算年: 0,
    };
    const header = [基準値Header, ...this.getHeaderBy基準値(e業績予測基準値)];
    const cfHeader = [基準値Header, ...this.getHeaderBy基準値(e業績予測基準値, true)];

    let list: 帳票データ[] = [];
    switch (v帳票Type) {
      case 帳票Type.科目別:
        // 科目別（シート数 = 部門数）
        list = this.create科目別帳票データs(
          {
            header,
            科目別計画s: v計画Plbscf.科目別損益計算書.科目別計画s.map((k) => ({
              ...k,
              list: 業績予測Service.getVisible科目別ListBy基準値(k.list, e業績予測基準値),
            })),
          },
          '損益計算書（科目別）業績予測',
          業績予測Service.計画書.c計画表示モード === E計画表示モード.年次計画
        );
        break;

      case 帳票Type.部門別:
        // 部門別の損益（シート数 = 部門数）
        list = v計画Plbscf.pls
          .filter((pl) => pl.部門pk !== undefined) // 合計以外
          .map((pl) => {
            const v部門 = this.部門s.find((b) => b.pk === pl.部門pk)!;
            return {
              sheetName: this.createXlsxSheetName(`損益計算書（部門別）業績予測-${v部門.c部門名}`),
              ...this.createPlbscf帳票データ(plbscfType, {
                ...pl,
                header,
                list: 業績予測Service.getVisibleListBy基準値(pl.list, e業績予測基準値),
              }),
            };
          });
        break;

      default:
        // 通常のplbscf（シート数 = 1）
        switch (plbscfType) {
          case PlbscfType.貸借対照表:
            list = [
              this.createPlbscf帳票データ(plbscfType, {
                header,
                list: 業績予測Service.getVisibleListBy基準値(v計画Plbscf.bs.list, e業績予測基準値),
              }),
            ];
            break;

          case PlbscfType.損益計算書:
            // 損益合計
            const pl = v計画Plbscf.pls.find((p) => p.部門pk === undefined)!;
            list = [
              this.createPlbscf帳票データ(plbscfType, {
                header,
                list: 業績予測Service.getVisibleListBy基準値(pl.list, e業績予測基準値),
              }),
            ];
            break;

          default:
            const typeList = v計画Plbscf.types.find((t) => t.type === plbscfType && t.部門pk === undefined)!;
            list = [
              this.createPlbscf帳票データ(plbscfType, {
                header: cfHeader,
                list: 業績予測Service.getVisibleCfListBy基準値(typeList.list, e業績予測基準値),
              }),
            ];
            break;
        }
        break;
    }
    this.setIndexedXlsxSheetNames(list);
    return list;
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 再計算し、画面表示用データを生成
   *
   * @returns
   */
  public calcAndCreate計画Plbscf(): 計画Plbscf {
    // 計算用科目別計画データを作成
    this.calc科目別計画s = this.部門s.map((d) => {
      const 計画List = this.計画PlbscfService!.getCalc科目別計画s.find((c) => c.部門pk === d.pk)!.list;
      return {
        部門pk: d.pk,
        list: this.create科目別計画ListItems(d.pk, 計画List),
      };
    });

    // plbscfの計算用データに計算結果を格納
    this.calcPlbscfValues();

    return this.create計画Plbscf();
  }

  /**
   * 画面表示用データを生成
   *
   * @returns
   */
  public create計画Plbscf(): 計画Plbscf {
    const is0円非表示 = AbstractデータService.計画書.cモニタリング0円非表示フラグ === E0円非表示フラグ.非表示;
    const header: 決算期TableHeader[] = [];

    // 計画書データの状態に応じて返却値を絞り込む
    // 行データ絞り込み
    const allTypes: PlbscfTypeList[] = this.calcTypeLists.map((t) => {
      let list = t.list.map((listItem) => {
        const yearValues = this.filterYearItems(listItem.yearValues);
        if ([PlbscfType.CF計算書, PlbscfType.CF計算書_計算式].includes(t.type)) {
          yearValues.forEach((yv) => (yv.withMonths = undefined));
        }
        return {
          ...listItem,
          yearValues,
        };
      });
      // 0円非表示フラグ
      if (is0円非表示) list = this.filterNotZero(list);
      if (t.type === PlbscfType.損益計算書) list = this.filter製造原価科目未使用(list);
      return {
        ...t,
        header,
        list,
      };
    });

    // 貸借・損益
    const bs: Bs = allTypes.find((t) => t.type === PlbscfType.貸借対照表)!;
    const pls: Pl[] = allTypes.filter((t) => t.type === PlbscfType.損益計算書);

    // 上記以外
    const types: PlbscfTypeList[] = allTypes.filter(
      (t) => ![PlbscfType.貸借対照表, PlbscfType.損益計算書].includes(t.type)
    );

    // 科目別
    const 科目別計画s = this.filter科目別計画(is0円非表示);

    return {
      bs,
      pls,
      types,
      補助科目別貸借対照表: {
        header: [],
        list: [],
      },
      科目別損益計算書: {
        header,
        科目別計画s,
      },
    };
  }

  /**
   * 業績予測基準値に応じてヘッダー取得
   *
   * @param e業績予測基準値
   * @param isCF計算書
   * @returns
   */
  public getHeaderBy基準値(e業績予測基準値: Eモニタリング業績予測基準値, isCF計算書 = false): 決算期TableHeader[] {
    const key = Eモニタリング業績予測基準値[e業績予測基準値] as keyof typeof Eモニタリング業績予測基準値;
    return isCF計算書 ? [this.yearlyHeader] : [this.yearlyHeader, ...this.業績予測Headers[key], ...this.monthlyHeaders];
  }

  // ------------------------------------------------------------------
  // override protected methods
  // ------------------------------------------------------------------

  // @override
  protected setTotal損益AndTranscribe(計画TypeLists: PlbscfTypeList[] = []): void {
    this.transcribe科目別ToPls();

    super.setTotal損益AndTranscribe(計画TypeLists);

    this.transcribe当期純利益ToBs();
  }

  // @override
  protected createMonitoringFor科目別計画(
    list: 科目別計画ListItem[],
    勘定科目Pk: number,
    通常科目Pk: number,
    科目別計画明細No?: number
  ): { yearItem: PlbscfYearItem; 業績予測: 業績予測 } | undefined {
    const yi = list
      .find(
        (li) =>
          li.勘定科目Pk === 勘定科目Pk &&
          li.通常科目Pk === 通常科目Pk &&
          li.config?.科目別計画明細No === 科目別計画明細No
      )!
      .yearItems.find((yi) => yi.year === this.会計期間Year)!;
    return {
      yearItem: {
        ...yi,
        type: YearItemType.readonly,
      },
      業績予測: {},
    };
  }

  // @override
  protected filterYearItems(yearItems: PlbscfYearItem[]): PlbscfYearItem[] {
    const year = this.会計期間Year;
    return yearItems
      .filter((yi) => [year, year - 1].includes(yi.year))
      .map((yi) => ({
        ...yi,
        withMonths: yi.year === year ? true : undefined,
        type: yi.year < year ? YearItemType.readonly : yi.type,
      }));
  }

  // @override
  protected isEditableMonth(monthIndex: number): boolean {
    return this.readonlyMonthIndex === null || monthIndex > this.readonlyMonthIndex;
  }

  // @override
  protected copyBy計画PlbscfService(
    データs: データListItemResponse[],
    option?: Record<string, string | number | AbstractデータService<unknown>>
  ): void {
    super.copyBy計画PlbscfService(データs, option);

    // 分析データは必要な分だけに絞っておく
    this.分析PlbscfTypeLists = this.分析PlbscfTypeLists.filter((t) =>
      [PlbscfType.貸借対照表, PlbscfType.損益計算書, PlbscfType.CF計算書, PlbscfType.CF計算書_計算式].includes(t.type)
    );
  }

  // @override
  protected create決算期Headers(): void {
    const year = this.会計期間Year;
    // 決算期
    this.all決算期 = 決算期Utility.getモニタリング決算期(決算期Utility.diffYearFrom0期(year), true);
    this.onlyYear決算期 = {
      header: this.all決算期.header.filter((h) => h.month === undefined),
      years: this.all決算期.years,
    };
    // ヘッダー
    this.yearlyHeader = {
      ...this.onlyYear決算期.header[this.onlyYear決算期.header.length - 1],
      isSummary: true,
    };
    this.monthlyHeaders = this.all決算期.header.filter((h) => h.決算年 === year && h.month !== undefined);
    this.業績予測Headers = {
      前期: 決算期Utility.create差異比率Header('予測', '前期'),
      計画: 決算期Utility.create差異比率Header('予測', '計画'),
    };
  }
}
